import { useState } from "react";
import {
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  useWindowDimensions,
} from "react-native";
import { Activity, CalendarDays, Check, PencilLine } from "lucide-react-native";
import {
  Dialog,
  DialogActions,
  DialogHeader,
  DialogIcon,
  DialogPrimaryButton,
  DialogSecondaryButton,
  MoonFieldInput,
  MoonNumericField,
  MoonDateField,
  MoonFormField,
} from "@louise/moon-ui";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { useCreateTestResult } from "@src/packages/test-results/hooks/useTestResults";
import { TestResultDocumentAction } from "./TestResultDocumentAction";
import type { SelectedFile } from "@src/packages/document-vault/DocumentFileDropZone";
import {
  useAfcResults,
  type AfcResult,
} from "@src/packages/homepage/utils/ovarianReserveInputs";

const AFC_ILLUSTRATION =
  require("@src/apps/moon/assets/images/you-icons/test-tubes.png") as number;

export type AfcSelection = {
  value: number;
  sourceId?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (selection: AfcSelection) => void;
  onClear?: () => void;
  onOpenDocuments: (files: SelectedFile[]) => void;
  currentSourceId?: string | null;
  hasActiveAfc?: boolean;
};

function formatDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function ExistingResultRow({
  result,
  selected,
  onPress,
}: {
  result: AfcResult;
  selected: boolean;
  onPress: () => void;
}) {
  const capturePress = usePressCapture("ExistingResultRow");
  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(result.value), "afc_source_option");
        onPress();
      }}
      accessibilityRole="button"
      style={({ pressed }) => ({
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: selected
          ? "rgba(92, 52, 32, 0.35)"
          : "rgba(92, 52, 32, 0.12)",
        backgroundColor: pressed
          ? "rgba(255,255,255,0.72)"
          : selected
            ? "rgba(255,255,255,0.62)"
            : "rgba(255,255,255,0.42)",
        paddingHorizontal: 12,
        paddingVertical: 10,
      })}
    >
      <View style={{ flex: 1 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            lineHeight: 20,
            color: EMBER.colors.text,
            fontWeight: "700",
          }}
        >
          {result.value}
        </Text>
        {result.date ? (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 16,
              color: EMBER.colors.textMid,
              marginTop: 2,
            }}
          >
            {formatDate(result.date)}
          </Text>
        ) : null}
      </View>
      {selected ? (
        <Check size={16} color={EMBER.colors.text} strokeWidth={2} />
      ) : null}
    </Pressable>
  );
}

export function AfcSourceSheet({
  open,
  onClose,
  onSelect,
  onClear,
  onOpenDocuments,
  currentSourceId,
  hasActiveAfc,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("AfcSourceSheet");
  const ns = "homepage.afc_source_sheet";
  const { height } = useWindowDimensions();
  const existing = useAfcResults();
  const createTestResult = useCreateTestResult();
  const dialogMaxHeight = Math.min(760, Math.max(480, height * 0.85));

  const [value, setValue] = useState<number | null>(null);
  const [date, setDate] = useState<string>(
    new Date().toISOString().slice(0, 10),
  );
  const [notes, setNotes] = useState("");

  const canSave = value !== null && value >= 0 && !createTestResult.isPending;

  const highValueWarning = value !== null && value > 40;

  const handleClose = () => {
    setValue(null);
    setDate(new Date().toISOString().slice(0, 10));
    setNotes("");
    onClose();
  };

  const handleSelectExisting = (result: AfcResult) => {
    onSelect({ value: result.value, sourceId: result.id });
    handleClose();
  };

  const handleAddNew = async () => {
    if (value === null || value < 0) return;
    try {
      await createTestResult.mutateAsync({
        name: "Antral Follicle Count",
        type: "imaging",
        imagingTestCategory: "antral_follicle_count",
        patientValue: String(Math.round(value)),
        valueType: "numeric",
        date: date || null,
        notes: notes.trim() || null,
      });
      onSelect({ value: Math.round(value) });
      handleClose();
    } catch {
      // surface via mutation error state
    }
  };

  const handleOpenDocuments = (files: SelectedFile[]) => {
    handleClose();
    onOpenDocuments(files);
  };

  return (
    <Dialog
      visible={open}
      onClose={handleClose}
      closeButton="minimal"
      containerStyle={{
        width: "90%",
        maxWidth: 420,
        maxHeight: dialogMaxHeight,
      }}
      surfaceStyle={{ maxHeight: dialogMaxHeight }}
      contentStyle={{
        paddingTop: 24,
        paddingHorizontal: 24,
        paddingBottom: 24,
        maxHeight: dialogMaxHeight,
      }}
    >
      <View style={{ flex: 1, minHeight: 0 }}>
        <DialogIcon
          source={AFC_ILLUSTRATION}
          width={56}
          height={62}
          style={{ marginBottom: 16 }}
        />
        <DialogHeader
          title={t(`${ns}.title`)}
          subtitle={t(`${ns}.subtitle`)}
          style={{ width: "100%", marginBottom: 16 }}
          titleStyle={{ textAlign: "center", width: "100%" }}
          subtitleStyle={{ textAlign: "center", width: "100%" }}
        />
        <ScrollView
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          style={{ flexShrink: 1 }}
          contentContainerStyle={{ gap: 20, paddingBottom: 4 }}
        >
          <TestResultDocumentAction
            onOpenDocuments={handleOpenDocuments}
            primaryLabel={t("care.beta_result.documents_drop_action")}
            secondaryLabel={t("care.beta_result.documents_drop_hint")}
            style={{ marginBottom: 0 }}
          />

          {existing.length > 0 ? (
            <MoonFormField label={t(`${ns}.existing_section`)}>
              <View style={{ gap: 8 }}>
                {existing.map((result) => (
                  <ExistingResultRow
                    key={result.id}
                    result={result}
                    selected={result.id === currentSourceId}
                    onPress={() => handleSelectExisting(result)}
                  />
                ))}
              </View>
            </MoonFormField>
          ) : null}

          <View style={{ gap: 12 }}>
            <MoonNumericField
              label={t(`${ns}.value_label`)}
              labelIcon={Activity}
              labelHint={t(`${ns}.value_hint`)}
              value={value}
              onChange={setValue}
              placeholder="0"
              decimals={0}
              min={0}
              max={100}
            />
            <MoonDateField
              label={t(`${ns}.date_label`)}
              labelIcon={CalendarDays}
              value={date}
              onChange={setDate}
            />
            <MoonFormField
              label={t(`${ns}.notes_label`)}
              labelIcon={PencilLine}
            >
              <MoonFieldInput paddingX={16}>
                <TextInput
                  className="font-dm-sans"
                  value={notes}
                  onChangeText={setNotes}
                  placeholder={t(`${ns}.notes_placeholder`)}
                  placeholderTextColor={EMBER.colors.textFaint}
                  multiline
                  numberOfLines={3}
                  textAlignVertical="top"
                  style={{
                    fontSize: 14,
                    color: EMBER.colors.text,
                    paddingVertical: 14,
                    minHeight: 72,
                  }}
                />
              </MoonFieldInput>
            </MoonFormField>
            {highValueWarning ? (
              <View
                style={{
                  marginTop: 4,
                  paddingHorizontal: 10,
                  paddingVertical: 8,
                  borderRadius: 10,
                  borderWidth: 1,
                  borderColor: "rgba(180, 83, 9, 0.25)",
                  backgroundColor: "rgba(254, 243, 199, 0.55)",
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    lineHeight: 17,
                    color: "#78350F",
                    fontWeight: "500",
                  }}
                >
                  {t(`${ns}.high_value_warning`)}
                </Text>
              </View>
            ) : null}
            {createTestResult.isError ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  color: "#B45309",
                  marginTop: 4,
                }}
              >
                {t(`${ns}.save_error`)}
              </Text>
            ) : null}
          </View>

          {hasActiveAfc && onClear ? (
            <Pressable
              onPress={() => {
                capturePress(
                  pressLabel(t(`${ns}.remove_afc`)),
                  `${ns}.remove_afc`,
                );
                onClear();
                handleClose();
              }}
              accessibilityRole="button"
              style={({ pressed }) => ({
                alignSelf: "flex-start",
                marginTop: 4,
                paddingHorizontal: 12,
                paddingVertical: 8,
                borderRadius: 999,
                borderWidth: 1,
                borderColor: "rgba(92, 52, 32, 0.14)",
                backgroundColor: pressed
                  ? "rgba(255,255,255,0.72)"
                  : "rgba(255,255,255,0.42)",
              })}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  lineHeight: 16,
                  fontWeight: "700",
                  color: EMBER.colors.text,
                }}
              >
                {t(`${ns}.remove_afc`)}
              </Text>
            </Pressable>
          ) : null}
        </ScrollView>
        <DialogActions style={{ marginTop: 14 }}>
          <DialogSecondaryButton
            label={t("care.beta_result.cancel")}
            onPress={handleClose}
            flex={1}
          />
          <DialogPrimaryButton
            label={
              createTestResult.isPending
                ? t(`${ns}.saving`)
                : t("care.beta_result.save")
            }
            onPress={handleAddNew}
            loading={createTestResult.isPending}
            disabled={!canSave}
            flex={1}
          />
        </DialogActions>
      </View>
    </Dialog>
  );
}
